import React, { FC, useMemo } from 'react';
import {
	alephToast,
	AvatarSize,
	Button,
	ButtonAppearance,
	ButtonColor,
	LoaderBars,
	NotificationToast,
	NotificationType,
	RemoveIcon,
	StarEmpty,
	StarFull,
	isReddit, isSpotify,
	NoValuePlaceholder,
} from '@aleph/shared-components';
import {
	CancelUserInvitationDocument,
	RedditUserGroupsListDocument,
	RevokeUserFromGroupDocument,
	UpdateUserGroupUserRoleDocument,
	UserAccountLevel,
	UserGroupRole,
	UserGroupRoleInput,
} from 'wb-reddit/graphql/generated';
import {
	UserGroupAccessRole,
	AuthGraphUpdateUserRoleInUserGroupDocument,
	AuthGraphRevokeUserFromGroupDocument,
} from 'wb-spotify/graphql/authgraph/generated';
import { useMutation } from '@apollo/client';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { userHasOneOfRoles } from 'wb-reddit/app/routes/utils';
import EntityOwnerDisplay from 'wb-reddit/app/common/components/EntityOwnerDisplay';
import EntityEditIconNotAllowed from 'wb-reddit/app/common/components/EntityEditIconNotAllowed';
import RedditEntityImage from 'wb-reddit/app/common/components/entityImage';
import { TeamRow, TeamUser } from 'wb-reddit/app/routes/teams/container';
import { useTranslation } from 'react-i18next';

const TeamAccessUserListItem: FC<{
	user?: TeamUser,
	group?: TeamRow,
	onUserDisconnected: (userId: string) => void,
	onUserRoleChanged: (userId: string, userRole: UserGroupRoleInput) => void,
	hideActions?: boolean,
	disabled?: boolean
}> = ({
	user, group, onUserDisconnected, hideActions, onUserRoleChanged, disabled,
}) => {
	const { t } = useTranslation();
	const [setUserRole, { loading: settingRole }] = useMutation(
		isReddit() ? UpdateUserGroupUserRoleDocument : AuthGraphUpdateUserRoleInUserGroupDocument,
		{ errorPolicy: 'all' },
	);
	const [revokeUserFromGroup, { loading: revokingAccess }] = useMutation(
		isReddit() ? RevokeUserFromGroupDocument : AuthGraphRevokeUserFromGroupDocument,
		{ errorPolicy: 'all' },
	);
	const [cancelUserInvitation, { loading: cancellingAccess }] = useMutation(CancelUserInvitationDocument, {
		errorPolicy:         'all',
		refetchQueries:      [RedditUserGroupsListDocument],
		awaitRefetchQueries: true,
	});

	const currentUser = useUserMetaData();

	const fullName = useMemo(() => {
		if (isReddit()) return user?.user?.userFullName;

		// @ts-ignore TODO
		return `${user?.user?.userName} ${user?.user?.userSurname || ''}`;
	}, [user]);

	if (!group || !user) {
		return null;
	}

	const owner = group.users
		?.find?.(currUser => currUser?.userGroupUserRole === UserGroupRole.Owner)?.user ?? currentUser;

	const onSetUserRole = async () => {
		const newUserRole = user.userGroupUserRole === UserGroupRole.Admin
			? UserGroupRoleInput.Member
			: UserGroupRoleInput.Admin;
		// @ts-ignore TODO
		const authUserRole = user.userGroupAccessRole === UserGroupAccessRole.Admin
			? UserGroupAccessRole.Member
			: UserGroupAccessRole.Admin;
		const responseData = await setUserRole({
			// @ts-ignore TODO
			variables: {
				userGroupId:                                       group.userGroupId ?? '',
				userId:                                            user.user.userId ?? '',
				// @ts-ignore TODO
				[isReddit() ? 'userRole' : 'userGroupAccessRole']: isReddit() ? newUserRole : authUserRole,
			},
		});

		if (responseData.errors) {
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={t('sc: action err', { action: 'saving user role' })}
				/>,
			);

			return;
		}

		alephToast(
			<NotificationToast
				type={NotificationType.Success}
				// TODO translate
				title={<div>Role of user <strong>{user.user?.userFullName}</strong> successfully updated</div>}
			/>,
		);

		// @ts-ignore
		onUserRoleChanged(user.user?.userId ?? '', isReddit() ? newUserRole : authUserRole);
	};

	const disconnectUser = async () => {
		if (isReddit() && group.userGroupId) {
			const { errors: userRevokeErrors } = !user?.userGroupPendingInviteHash ? await revokeUserFromGroup({
				variables: {
					userId:      user?.user?.userId ?? '',
					userGroupId: group.userGroupId ?? '',
				},
			}) : { errors: undefined };

			const { errors: userCancelInvitationErrors } = user?.userGroupPendingInviteHash
				? await cancelUserInvitation({
					variables: {
						pendingInviteHash: user?.userGroupPendingInviteHash ?? '',
					},
				}) : { errors: undefined };

			if (userRevokeErrors || userCancelInvitationErrors) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={t('sc: action err', { action: 'revoking access' })}
					/>,
				);

				return;
			}

			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					// TODO translate
					title={<div>User <strong>{user?.user?.userFullName}</strong> revoked access from <strong>{group.userGroupName}</strong></div>}
				/>,
			);
		}

		if (isSpotify() && group.userGroupId) {
			const { errors: userRevokeErrors } = await revokeUserFromGroup({
				variables: {
					userId:      user?.user?.userId ?? '',
					userGroupId: group.userGroupId ?? '',
				},
			});

			if (userRevokeErrors) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						// @ts-ignore TODO
						title={userRevokeErrors?.[0]?.form?.userGroupId?.message ?? t('sc: action err', { action: 'revoking access' })}
					/>,
				);

				return;
			}

			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					// TODO translate
					title={<div>User <strong>{user?.user?.userFullName}</strong> revoked access from <strong>{group.userGroupName}</strong></div>}
				/>,
			);
		}

		onUserDisconnected(user.user?.userId);
	};

	const domain = user?.user?.userEmail?.split('@')[1];

	return (
		<div
			key={user?.user?.userId}
			className="text-[16px] font-medium h-[48px] flex px-[8px] items-center border-b border-b-gray-200 last:border-0 hover:bg-gray-100"
			id={user?.user?.userId}
		>
			<div className="flex flex-1 gap-[6px] whitespace-nowrap items-center h-full overflow-hidden">
				<RedditEntityImage
					size={AvatarSize.Small}
					entity={user?.user}
				/>

				<div className="overflow-hidden overflow-ellipsis">
					{fullName}
				</div>
			</div>
			<NoValuePlaceholder
				placeholder={domain ? `@${domain}` : undefined}
			/>

			{!hideActions
				&& <>
					{(group.userGroupId && !user?.userGroupPendingInviteHash) && (
						<div className="w-[80px] flex items-center justify-center">
							{settingRole
								? <Button
									leftNode={<LoaderBars className="animate-spin" />}
									disabled
								/> : <>
									{(userHasOneOfRoles(user?.user, [UserAccountLevel.User])
									// @ts-ignore  TODO fix this
										|| [UserGroupAccessRole.Admin, UserGroupAccessRole.Member].includes(user?.userGroupAccessRole))
											&& <Button
												leftNode={
													([UserGroupRole.Owner, UserGroupRole.Admin].includes(user?.userGroupUserRole)
															// @ts-ignore TODO
															|| [UserGroupAccessRole.Owner, UserGroupAccessRole.Admin].includes(user?.userGroupAccessRole)
													)
														? <StarFull />
														: <StarEmpty/>
												}
												appearance={ButtonAppearance.GHOST}
												color={ButtonColor.GRAY}
												disabled={user?.userGroupUserRole === UserGroupRole.Owner || disabled}
												onClick={onSetUserRole}
											/>
									}

									{(userHasOneOfRoles(
										user?.user,
										[UserAccountLevel.Agency, UserAccountLevel.Admin, UserAccountLevel.Advertiser],
									)
									// @ts-ignore TODO
									|| [UserGroupAccessRole.Owner].includes(user?.userGroupAccessRole)) && <StarFull
										width={16}
										height={16}
									/>
									}

									{user?.user?.userAccountLevel === UserAccountLevel.ReadonlyUser
											&& (
												<EntityEditIconNotAllowed
													tooltipText={t('sc: teams.admin_creation_warning', 'Users with read-only access cannot be team admins.')}
													icon={
														<StarEmpty
															width={16}
															height={16}
														/>
													}
												/>
											)}
								</>
							}
						</div>
					)}

					{(group?.userGroupId && user?.userGroupPendingInviteHash) && (
						<div className="w-[80px] flex items-center justify-center">
							<span className="text-[12px] text-gray-600">{t('re: advertiser.status.pending', 'Pending')}</span>
						</div>
					)
					}

					<div className="w-[60px] flex justify-center">
						{(revokingAccess || cancellingAccess) ? <Button
							leftNode={
								<LoaderBars className="animate-spin" />
							}
							disabled
						/> : user?.user?.userId === owner?.userId
							? <EntityOwnerDisplay />
							: <Button
								leftNode={<RemoveIcon />}
								appearance={ButtonAppearance.GHOST}
								color="gray"
								onClick={disconnectUser}
								disabled={disabled}
							/>
						}
					</div>
				</>
			}
		</div>
	);
};

export default TeamAccessUserListItem;
